import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid,
  Button, Typography
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGridRowActionsMenu } from "../components/DataGridRowActionsMenu.js";
import { DialogHelp } from "../components/DialogHelp.js";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/PageHeader.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { EmptyState } from "../components/EmptyState.js";
import { ActiveChip } from "../components/StatusChip.js";
import { useApi } from "../hooks/useApi.js";
import type { Supplier } from "@kappa-v2/contracts";

interface FormValues { companyName: string; VATNumber: string; phone: string; email: string }
const EMPTY: FormValues = { companyName: "", VATNumber: "", phone: "", email: "" };

export function SuppliersPage() {
  const { t } = useTranslation();
  const { call } = useApi();
  const schema = Yup.object({
    companyName: Yup.string().required(t("suppliers.form.nameRequired")),
    VATNumber: Yup.string(),
    phone: Yup.string(),
    email: Yup.string().email(t("suppliers.form.invalidEmail"))
  });
  const [rows, setRows] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Supplier | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Supplier | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    call<{ data: Supplier[] }>("/api/v1/suppliers")
      .then(r => setRows(r.data ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [call]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const formik = useFormik<FormValues>({
    initialValues: EMPTY,
    validationSchema: schema,
    onSubmit: async (values) => {
      setSaving(true);
      const body: Partial<Supplier> = {
        companyName: values.companyName,
        VATNumber: values.VATNumber || undefined,
        active: true,
        contacts: values.phone ? [{ telephoneNumber: values.phone, isMain: true }] : [],
        emails: values.email ? [{ email: values.email, isMain: true }] : []
      };
      try {
        if (editTarget?._id) {
          await call(`/api/v1/suppliers/${editTarget._id}`, { method: "PUT", body });
        } else {
          await call("/api/v1/suppliers", { method: "POST", body });
        }
        setDialogOpen(false); fetchAll();
      } finally { setSaving(false); }
    }
  });

  const openAdd = () => { setEditTarget(null); formik.resetForm({ values: EMPTY }); setDialogOpen(true); };
  const openEdit = (s: Supplier) => {
    setEditTarget(s);
    formik.resetForm({ values: {
      companyName: s.companyName ?? "",
      VATNumber: s.VATNumber ?? "",
      phone: s.contacts?.[0]?.telephoneNumber ?? "",
      email: s.emails?.[0]?.email ?? ""
    }});
    setDialogOpen(true);
  };
  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    await call(`/api/v1/suppliers/${deleteTarget._id}`, { method: "DELETE" });
    setDeleteTarget(null); fetchAll();
  };

  const filtered = rows.filter(r =>
    `${r.companyName ?? ""} ${r.VATNumber ?? ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: "name", headerName: t("suppliers.companyName"), flex: 1, minWidth: 200,
      renderCell: (params: GridRenderCellParams<Supplier>) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{params.row.companyName ?? "—"}</Typography>
          <Typography variant="caption" color="text.secondary">{t("suppliers.vat", { value: params.row.VATNumber ?? "—" })}</Typography>
        </Box>
      )
    },
    {
      field: "contact", headerName: t("suppliers.contact"), flex: 1, minWidth: 160,
      renderCell: (params: GridRenderCellParams<Supplier>) => (
        <Box>
          <Typography variant="body2">{params.row.contacts?.[0]?.telephoneNumber ?? "—"}</Typography>
          <Typography variant="caption" color="text.secondary">{params.row.emails?.[0]?.email ?? ""}</Typography>
        </Box>
      )
    },
    {
      field: "active", headerName: t("common.status"), width: 120,
      renderCell: (params: GridRenderCellParams<Supplier>) => <ActiveChip active={params.row.active} />
    },
    {
      field: "actions",
      headerName: "",
      width: 56,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Supplier>) => (
        <DataGridRowActionsMenu
          ariaLabel={t("suppliers.actionsAria", { name: params.row.companyName ?? t("suppliers.fallbackName") })}
          items={[
            {
              key: "edit",
              label: t("suppliers.editAction"),
              description: t("suppliers.editActionDesc"),
              icon: <EditIcon fontSize="small" />,
              onClick: () => openEdit(params.row)
            },
            {
              key: "delete",
              label: t("suppliers.deleteAction"),
              description: t("suppliers.deleteActionDesc"),
              icon: <DeleteIcon fontSize="small" />,
              destructive: true,
              dividerBefore: true,
              onClick: () => setDeleteTarget(params.row)
            }
          ]}
        />
      )
    }
  ];

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader title={t("suppliers.title")} subtitle={t("suppliers.subtitle")} actionLabel={t("suppliers.newSupplier")} onAction={openAdd} onRefresh={fetchAll} />
      <Box sx={{ p: 3, flex: 1 }}>
        <Card>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)" }}>
            <TextField size="small" placeholder={t("suppliers.searchPlaceholder")}
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
              sx={{ width: 320 }}
            />
          </Box>
          {!loading && filtered.length === 0
            ? <EmptyState title={t("suppliers.none")} actionLabel={t("suppliers.addSupplier")} onAction={openAdd} />
            : <DataGrid rows={filtered} columns={columns} getRowId={r => r._id ?? Math.random().toString()}
                loading={loading} autoHeight rowHeight={60} pageSizeOptions={[25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                disableRowSelectionOnClick
                sx={{ border: "none", "& .MuiDataGrid-cell": { borderColor: "rgba(0,0,0,.04)" } }}
              />
          }
        </Card>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            {editTarget ? t("suppliers.editSupplier") : t("suppliers.newSupplier")}
            <DialogHelp steps={[{ title: t("help.formTitle"), description: t("help.forms.suppliers") }]} />
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t("suppliers.form.companyLabel")}
                  {...formik.getFieldProps("companyName")}
                  error={formik.touched.companyName && Boolean(formik.errors.companyName)}
                  helperText={formik.touched.companyName && formik.errors.companyName}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t("suppliers.form.vatLabel")} {...formik.getFieldProps("VATNumber")} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label={t("suppliers.form.phoneLabel")} {...formik.getFieldProps("phone")} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label={t("suppliers.form.emailLabel")}
                  {...formik.getFieldProps("email")}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? t("common.saving") : editTarget ? t("common.save") : t("common.add")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        message={t("suppliers.deleteConfirm", { name: deleteTarget?.companyName })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
